class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.footerObserver = null;
      this.targetElement = null;
      this.footerElement = null;
      this.isVisible = false;
      this.variantContainer = null;
      this.toggleBtn = null;
      this.isExpanded = false;
      this.productInfoVisible = false;
      this.footerVisible = false;
      this.abortController = new AbortController();
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.footerObserver) {
        this.footerObserver.disconnect();
      }
      if (this.toggleBtn) {
        this.toggleBtn.removeEventListener('click', this.toggleVariant.bind(this));
      }
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    init() {
      this.targetElement = document.querySelector('.product-information');
      this.footerElement = document.querySelector('custom-footer.footer');

      if (!this.targetElement) {
        console.warn('StickyAddToCart: .product-information element not found');
        return;
      }

      // Get variant container and toggle button
      this.selectBtn = this.querySelector('.sticky-add-to-cart__select-btn-container');
      this.atcBtn = this.querySelector('.sticky-add-to-cart__atc-btn-container')
      this.variantContainer = this.querySelector('.sticky-add-to-cart__variant-container');
      this.toggleBtn = this.querySelector('.sticky-add-to-cart__variant-toggle-btn');

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', this.toggleVariant.bind(this));
      }

      this.setupObserver();
      this.setupFooterObserver();
      this.setupVariantUpdateListener();

      // Initialize button states on load
      this.initializeButtonStates();
      
      // Initialize notify-me button data
      this.initializeNotifyMeData();

      if (this.selectBtn) {
        this.selectBtn.addEventListener('click', () => {
          this.selectBtn.style.display = 'none';
          if (this.atcBtn) this.atcBtn.style.display = 'block';
          this.openVariant();
        });
      }

    }

    setupObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1, // Require 10% visibility to prevent flickering
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.productInfoVisible = entry.isIntersecting;
          this.updateStickyVisibility();
        });
      }, options);

      this.observer.observe(this.targetElement);
    }

    setupFooterObserver() {
      if (!this.footerElement) {
        console.warn('StickyAddToCart: custom-footer.footer element not found');
        return;
      }

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1, // Require 10% visibility to prevent flickering
      };

      this.footerObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.footerVisible = entry.isIntersecting;
          this.updateStickyVisibility();
        });
      }, options);

      this.footerObserver.observe(this.footerElement);
    }

    updateStickyVisibility() {
      // Show sticky when:
      // 1. Product information is NOT visible (out of viewport)
      // 2. Footer is NOT visible (out of viewport)
      const shouldShow = !this.productInfoVisible && !this.footerVisible;
      
      if (shouldShow && !this.isVisible) {
        this.showSticky();
      } else if (!shouldShow && this.isVisible) {
        this.hideSticky();
      }
    }

    showSticky() {
      if (!this.isVisible) {
        this.isVisible = true;
        this.classList.remove('hide');
        this.classList.add('show');
        this.setAttribute('aria-hidden', 'false');
      }
    }

    hideSticky() {
      if (this.isVisible) {
        this.isVisible = false;
        this.classList.remove('show');
        this.classList.add('hide');
        this.setAttribute('aria-hidden', 'true');
        // Close variant drawer when hiding sticky cart
        this.closeVariant();
      }
    }


    toggleVariant() {
      if (this.isExpanded) {
        this.closeVariant();
      } else {
        this.openVariant();
      }
    }

    openVariant() {
      if (this.variantContainer && !this.isExpanded) {
        this.isExpanded = true;
        this.variantContainer.classList.add('expanded');
      }
    }

    closeVariant() {
      if (this.variantContainer && this.isExpanded) {
        this.isExpanded = false;
        this.variantContainer.classList.remove('expanded');
      }
    }

    setupVariantUpdateListener() {
      const { signal } = this.abortController;
      
      // Listen on document for variant updates from any section
      document.addEventListener('variant:update', this.onVariantUpdate.bind(this), { signal });
      
      // Also listen on the main product information section specifically
      const mainProductSection = document.querySelector('.product-information');
      if (mainProductSection) {
        mainProductSection.addEventListener('variant:update', this.onVariantUpdate.bind(this), { signal });
      }

      // Listen for changes in the sticky variant picker to sync back to main
      const stickyVariantPicker = this.querySelector('variant-picker');
      if (stickyVariantPicker) {
        stickyVariantPicker.addEventListener('variant:update', this.onStickyVariantChange.bind(this), { signal });
      }
    }

    onVariantUpdate(event) {
      // Only process events for the same product
      if (event.detail.data && event.detail.data.productId) {
        const stickyVariantPicker = this.querySelector('variant-picker');
        const stickyProductId = stickyVariantPicker?.dataset.productId;
        
        if (stickyProductId && stickyProductId !== event.detail.data.productId) {
          return;
        }
      }
      
      // Update the inventory status display
      // Coming-soon products always show "Coming Soon" — never overwrite it based on variant availability
      const isComingSoon = document.body.classList.contains('page-name-coming-soon');
      const availabilitySpan = this.querySelector('.available, .unavailable');

      if (availabilitySpan && event.detail.resource && !isComingSoon) {
        if (event.detail.resource.available) {
          availabilitySpan.className = 'available';
          availabilitySpan.textContent = 'In stock';
        } else {
          availabilitySpan.className = 'unavailable';
          availabilitySpan.textContent = 'Out of stock';
        }
      }

      // Update button states in the sticky component
      const atcBtnContainer = this.querySelector('.sticky-add-to-cart__atc-btn-container');
      const selectBtnContainer = this.querySelector('.sticky-add-to-cart__select-btn-container');
      const addToCartBtn = atcBtnContainer?.querySelector('button[type="submit"]');
      const notifyMeBtn = atcBtnContainer?.querySelector('.notify-me-btn');

      // Always update notify-me button data when variants change
      if (notifyMeBtn) {
        this.updateNotifyMeButtonData(notifyMeBtn, event);
      }

      if (event.detail.resource == null || event.detail.resource.available == false) {
        // Out of stock - show ATC container with notify button, hide select container
        if (atcBtnContainer) {
          atcBtnContainer.style.setProperty('display', 'block', 'important');
        }
        if (selectBtnContainer) {
          selectBtnContainer.style.setProperty('display', 'none', 'important');
        }
        if (addToCartBtn) addToCartBtn.style.display = 'none';
        if (notifyMeBtn) {
          notifyMeBtn.style.display = 'grid';
        }
      } else {
        // In stock - show ATC container with add to cart button, hide select container
        if (atcBtnContainer) {
          atcBtnContainer.style.setProperty('display', 'block', 'important');
        }
        if (selectBtnContainer) {
          selectBtnContainer.style.setProperty('display', 'none', 'important');
        }
        if (addToCartBtn) addToCartBtn.style.display = '';
        if (notifyMeBtn) notifyMeBtn.style.display = 'none';
      }
      
      // Also sync the sticky variant picker to match the selected variant
      this.syncVariantPickers(event);
    }

    syncVariantPickers(event) {
      const stickyVariantPicker = this.querySelector('variant-picker');
      if (!stickyVariantPicker || !event.detail.sourceId) return;

      // Only sync if the event came from a different variant picker (not the sticky one)
      const isFromStickyPicker = event.target && this.contains(event.target);
      if (isFromStickyPicker) return;

      // Find the matching option in the sticky picker and select it
      const targetOptionId = event.detail.sourceId;
      const targetElement = stickyVariantPicker.querySelector(`[data-option-value-id="${targetOptionId}"]`);
      
      if (targetElement && stickyVariantPicker.updateSelectedOption) {
        stickyVariantPicker.updateSelectedOption(targetElement);
      }
    }

    onStickyVariantChange(event) {
      // Find the main variant picker and sync it
      const mainVariantPicker = document.querySelector('.product-information variant-picker');
      if (!mainVariantPicker || !event.detail.sourceId) return;

      const targetOptionId = event.detail.sourceId;
      const targetElement = mainVariantPicker.querySelector(`[data-option-value-id="${targetOptionId}"]`);
      
      if (targetElement && mainVariantPicker.updateSelectedOption) {
        mainVariantPicker.updateSelectedOption(targetElement);
        
        // Manually trigger the variant change on the main picker
        const changeEvent = new Event('change', { bubbles: true });
        targetElement.dispatchEvent(changeEvent);
      }
    }

    updateNotifyMeButtonData(notifyMeBtn, event) {
      // Get product information from the sticky component
      const productTitle = this.getProductTitle();
      const productName = this.getProductName();
      const productId = this.getProductId();
      const productImage = this.getProductImage(event);
      const productPrice = this.getProductPrice();
      const productColor = this.getSelectedColor(event);

      // Update the data attributes on the notify-me button
      if (productTitle) notifyMeBtn.setAttribute('data-product-title', productTitle);
      if (productName) notifyMeBtn.setAttribute('data-product-name', productName);
      if (productId) notifyMeBtn.setAttribute('data-product-id', productId);
      if (productImage) notifyMeBtn.setAttribute('data-product-image', productImage);
      if (productPrice) notifyMeBtn.setAttribute('data-product-price', productPrice);
      if (productColor) notifyMeBtn.setAttribute('data-product-color', productColor);
    }

    getProductTitle() {
      // Try to get product title from various sources
      const titleElement = this.querySelector('.title');
      if (titleElement) return titleElement.textContent.trim();
      
      // Fallback to document title or meta
      const productScript = document.querySelector('script[type="application/ld+json"]');
      if (productScript) {
        try {
          const productData = JSON.parse(productScript.textContent);
          return productData.name;
        } catch (e) {}
      }
      
      return '';
    }

    getProductName() {
      // Get product name from title element or use title as fallback
      const titleElement = this.querySelector('.title');
      return titleElement ? titleElement.textContent.trim() : this.getProductTitle();
    }

    getProductId() {
      // Get product ID from variant picker or form
      const stickyVariantPicker = this.querySelector('variant-picker');
      if (stickyVariantPicker) {
        return stickyVariantPicker.dataset.productId;
      }
      
      // Fallback to main variant picker
      const mainVariantPicker = document.querySelector('.product-information variant-picker');
      if (mainVariantPicker) {
        return mainVariantPicker.dataset.productId;
      }
      
      return '';
    }

    getProductImage(event = null) {
      // Try to get image from variant event data first
      if (event && event.detail.resource && event.detail.resource.featured_media) {
        const mediaUrl = event.detail.resource.featured_media.preview_image?.src;
        if (mediaUrl) return mediaUrl;
      }
      
      // Get image from sticky component
      const imgElement = this.querySelector('.sticky-add-to-cart__img-container img');
      if (imgElement) return imgElement.src;
      
      // Fallback to main product image
      const mainImg = document.querySelector('.product-information img, .media-gallery img');
      if (mainImg) return mainImg.src;
      
      return '';
    }

    getProductPrice() {
      // Get price from price container
      const priceElement = this.querySelector('.price-container .price, .price');
      if (priceElement) {
        // Extract just the price number, removing currency symbols
        const priceText = priceElement.textContent.trim();
        return priceText.replace(/[^\d.,]/g, ''); // Keep only digits, dots, and commas
      }
      
      return '';
    }

    getSelectedColor(event = null) {
      // First try to get color from the main product-info variant picker since that's where changes come from
      const mainVariantPicker = document.querySelector('.product-information variant-picker');
      if (mainVariantPicker) {
        // Look for color option input in main picker
        const colorInputs = mainVariantPicker.querySelectorAll('input[name*="color" i], input[name*="colour" i]');
        for (const input of colorInputs) {
          if (input.checked) {
            return input.value;
          }
        }
        
        // If no specific color input, get the first checked input as fallback
        const checkedInput = mainVariantPicker.querySelector('input:checked');
        if (checkedInput) {
          return checkedInput.value;
        }
      }
      
      // Fallback to sticky variant picker
      const stickyVariantPicker = this.querySelector('variant-picker');
      if (stickyVariantPicker) {
        // Look for color option input
        const colorInputs = stickyVariantPicker.querySelectorAll('input[name*="color" i], input[name*="colour" i]');
        for (const input of colorInputs) {
          if (input.checked) {
            return input.value;
          }
        }
        
        // If no specific color input, try to get from any checked input as fallback
        const checkedInput = stickyVariantPicker.querySelector('input:checked');
        if (checkedInput) {
          return checkedInput.value;
        }
      }
      
      return '';
    }

    initializeButtonStates() {
      // Check the initial availability status from the server-rendered content
      const availabilitySpan = this.querySelector('.available, .unavailable');
      const isInStock = availabilitySpan?.classList.contains('available');
      
      const atcBtnContainer = this.querySelector('.sticky-add-to-cart__atc-btn-container');
      const selectBtnContainer = this.querySelector('.sticky-add-to-cart__select-btn-container');
      
      // Initially show "Select Size" button, the ATC container will be shown after variant selection
      if (atcBtnContainer) {
        atcBtnContainer.style.setProperty('display', 'none', 'important');
      }
      if (selectBtnContainer) {
        selectBtnContainer.style.setProperty('display', 'block', 'important');
      }
    }

    initializeNotifyMeData() {
      // Find the notify-me button and initialize its data
      const notifyMeBtn = this.querySelector('.notify-me-btn');
      if (notifyMeBtn) {
        const productTitle = this.getProductTitle();
        const productName = this.getProductName();
        const productId = this.getProductId();
        const productImage = this.getProductImage();
        const productPrice = this.getProductPrice();
        const productColor = this.getSelectedColor();

        // Set initial data attributes
        if (productTitle) notifyMeBtn.setAttribute('data-product-title', productTitle);
        if (productName) notifyMeBtn.setAttribute('data-product-name', productName);
        if (productId) notifyMeBtn.setAttribute('data-product-id', productId);
        if (productImage) notifyMeBtn.setAttribute('data-product-image', productImage);
        if (productPrice) notifyMeBtn.setAttribute('data-product-price', productPrice);
        if (productColor) notifyMeBtn.setAttribute('data-product-color', productColor);
      }
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);